import { useEffect, useRef, useState } from "react";

export function useWoundInfinitePagination({ hasMore, limit, onFetch }) {
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const loadMoreRef = useRef(null);
  const observerRef = useRef(null);
  const isFetchingRef = useRef(false);
  const isResettingRef = useRef(false);

  const fetchNextPage = () => {
    if (isFetchingRef.current) return;
    if (isResettingRef.current && offset !== 0) return;
    if (hasMore === false && offset !== 0) return;

    isFetchingRef.current = true;
    setLoading(true);

    onFetch(offset).finally(() => {
      isFetchingRef.current = false;
      setLoading(false);
      if (offset === 0) {
        isResettingRef.current = false;
      }
    });
  };

  useEffect(() => {
    fetchNextPage();
  }, [offset, refreshKey]);

  const resetPagination = () => {
    isResettingRef.current = true;
    setRefreshKey(prev => prev + 1);
    setOffset(0);
  };

  useEffect(() => {
    if (!loadMoreRef.current) return;

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isFetchingRef.current && !isResettingRef.current && hasMore) {
          setOffset((prev) => prev + limit);
        }
      },
      { root: null, rootMargin: "300px", threshold: 0 }
    );

    observerRef.current.observe(loadMoreRef.current);

    return () => observerRef.current?.disconnect();
  }, [hasMore, offset, limit]);

  useEffect(() => {
    const onScroll = () => {
      if (isFetchingRef.current || isResettingRef.current || hasMore === false) return;

      const nearBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 300;

      if (nearBottom) {
        setOffset((prev) => prev + limit);
      }
    };

    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [hasMore, offset, limit]);

  return {
    loading,
    loadMoreRef,
    resetPagination,
  };
}
